"use client";

import { type ModuleTeamGrid } from "@lib/types";
import { useRef, useState } from "react";

import PortableText from "@/components/shared/portable-text";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { cn } from "@/lib/ui/cn";
import { getProseClasses } from "@/lib/ui/colorTheme";

import { TeamCarouselModal } from "../TeamCarousel/Modal";

type TeamGridCardProps = {
  item: NonNullable<ModuleTeamGrid["items"]>[number];
  textColorClasses?: string;
};

export function TeamGridCard({ item, textColorClasses }: TeamGridCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openedFromTouchRef = useRef(false);

  const hasBio = item.bio && item.bio.length > 0;
  const hasInactiveImage = !!item.inactiveImage?.image;

  const openModal = () => {
    if (hasBio) {
      setIsModalOpen(true);
    }
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!hasBio || event.pointerType !== "touch") {
      return;
    }

    openedFromTouchRef.current = true;
    openModal();
  };

  const handleClick = () => {
    if (openedFromTouchRef.current) {
      openedFromTouchRef.current = false;
      return;
    }

    openModal();
  };

  return (
    <>
      <div
        className={cn(
          "group flex flex-col gap-4 transition-all duration-300",
          hasBio && "cursor-pointer",
          hasBio && "hover:-translate-y-1",
        )}
        onPointerUp={handlePointerUp}
        onClick={handleClick}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && hasBio) {
            e.preventDefault();
            setIsModalOpen(true);
          }
        }}
        tabIndex={hasBio ? 0 : undefined}
        role={hasBio ? "button" : undefined}
        aria-label={hasBio ? "View bio" : undefined}
      >
        {(item.image || hasInactiveImage) && (
          <div className="relative aspect-square w-full overflow-hidden rounded-lg transition-shadow duration-300 group-hover:shadow-lg">
            <div
              className={cn(
                "relative h-full w-full transition-all duration-300",
                hasBio && "group-hover:scale-105",
              )}
            >
              {item.image && (
                <div
                  key="active-image"
                  className={cn(
                    "absolute inset-0 transition-opacity duration-300",
                    hasInactiveImage
                      ? "pointer-events-none opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:pointer-events-auto [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100"
                      : "opacity-100 grayscale [@media(hover:hover)_and_(pointer:fine)]:group-hover:grayscale-0",
                  )}
                >
                  <ResponsiveImage
                    key="active-img"
                    src={item.image}
                    fill
                    className="object-cover object-center transition-transform duration-300"
                    imageOptions={{ desktopWidth: 400 }}
                  />
                </div>
              )}

              {hasInactiveImage && (
                <div
                  key="inactive-image"
                  className={cn(
                    "absolute inset-0 transition-opacity duration-300",
                    "pointer-events-none opacity-100 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-0",
                  )}
                >
                  <ResponsiveImage
                    key="inactive-img"
                    src={item.inactiveImage}
                    fill
                    className="object-cover object-center transition-transform duration-300"
                    imageOptions={{ desktopWidth: 400 }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {item.content && (
          <div
            className={cn("transition-opacity duration-300", hasBio && "group-hover:opacity-80")}
          >
            <PortableText
              value={item.content}
              className={cn("prose prose-p:my-0 typography-text-medium-normal", textColorClasses)}
            />
          </div>
        )}
      </div>

      {hasBio && (
        <TeamCarouselModal
          item={item}
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          textColorClasses={getProseClasses(false)}
        />
      )}
    </>
  );
}
